import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';

export interface ClosurePeriod {
  id: string;
  date_debut: string;
  date_fin: string;
  raison: string | null;
  affected_product_ids: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useClosurePeriods() {
  const [closures, setClosures] = useState<ClosurePeriod[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchClosures = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('closure_periods' as any)
        .select('*')
        .order('date_debut', { ascending: false });

      if (error) throw error;
      setClosures((data as any[]) || []);
    } catch (error) {
      logger.error('Erreur chargement fermetures', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClosures();
  }, [fetchClosures]);

  const createClosure = useCallback(async (data: {
    date_debut: string;
    date_fin: string;
    raison?: string;
    affected_product_ids: string[];
  }) => {
    try {
      const { error } = await supabase
        .from('closure_periods' as any)
        .insert(data as any);
      if (error) throw error;
      toast.success('Période de fermeture ajoutée');
      await fetchClosures();
    } catch (error) {
      logger.error('Erreur création fermeture', error);
      toast.error('Erreur lors de la création');
    }
  }, [fetchClosures]);

  const deleteClosure = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('closure_periods' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast.success('Période de fermeture supprimée');
      await fetchClosures();
    } catch (error) {
      logger.error('Erreur suppression fermeture', error);
      toast.error('Erreur lors de la suppression');
    }
  }, [fetchClosures]);

  const toggleClosure = useCallback(async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('closure_periods' as any)
        .update({ is_active: !isActive } as any)
        .eq('id', id);
      if (error) throw error;
      toast.success(isActive ? 'Fermeture désactivée' : 'Fermeture activée');
      await fetchClosures();
    } catch (error) {
      logger.error('Erreur mise à jour fermeture', error);
    }
  }, [fetchClosures]);

  return { closures, loading, createClosure, deleteClosure, toggleClosure, fetchClosures };
}
